import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';

/** Module-level counter so every instance's `<defs>` ids stay unique — two
 * `<squadbox-omark>` on the page (live + completed) must not share a mask. */
let nextInstanceId = 0;

/** Rotation centres: the hex/gradient turn around the viewBox centre, the
 * mask's cut-out around its own (they are not the same point). */
const CX = 16;
const CY = 15.405;
const INNER_CX = 15.9885;
const INNER_CY = 15.3317;

/** The gradient's three stops: all `BASE` at rest, tinted towards these while
 * working. */
const BASE = '#7579ff';
const MID = '#a27cff';
const END = '#b66dff';

/** Time scale of the run loop, and how long the landing takes. */
const SPEED = 1.6;
const SETTLE_MS = 550;

interface Pose {
  hexRot: number;
  innerRot: number;
  gradRot: number;
  tint: number;
}

const REST: Pose = { hexRot: 0, innerRot: 0, gradRot: 0, tint: 0 };

/** The pose at `t` seconds of run time: one hex turn per 8s, the cut-out
 * counter-turning every 12s, the gradient every 6s — the three periods are
 * coprime enough that the mark never repeats a frame for a whole minute. */
const poseAt = (t: number): Pose => ({
  hexRot: (360 * t) / 8,
  innerRot: (-360 * t) / 12,
  gradRot: (360 * t) / 6,
  tint: 1,
});

const easeOut = (x: number): number => 1 - Math.pow(1 - x, 3);

const lerpPose = (a: Pose, b: Pose, k: number): Pose => ({
  hexRot: a.hexRot + (b.hexRot - a.hexRot) * k,
  innerRot: a.innerRot + (b.innerRot - a.innerRot) * k,
  gradRot: a.gradRot + (b.gradRot - a.gradRot) * k,
  tint: a.tint + (b.tint - a.tint) * k,
});

/** Per-channel linear mix of two `#rrggbb` colors. */
const mixHex = (from: string, to: string, k: number): string => {
  const channel = (index: number): number => {
    const a = parseInt(from.slice(1 + index * 2, 3 + index * 2), 16);
    const b = parseInt(to.slice(1 + index * 2, 3 + index * 2), 16);
    return Math.round(a + (b - a) * k);
  };
  return `rgb(${channel(0)},${channel(1)},${channel(2)})`;
};

/**
 * SB-3651 — the exact Squadbox "O" mark from the artifact (gradient hex
 * masked by the rounded-rect cut-out, not a plain filled path). Runs the
 * artifact's "Rotation" animation while {@link working} is true, then settles
 * onto the next hex vertex and fades the tint out once the answer starts
 * streaming. Purely decorative, so it is `aria-hidden` — the live region
 * announcing the answer lives beside it in `conversation.page.ts`.
 *
 * The frame loop writes the four attributes directly and never touches a
 * signal, so an animating mark costs no change detection.
 */
@Component({
  selector: 'squadbox-omark',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      [class.working]="working()"
      viewBox="0 0 32 30.811"
      width="32"
      height="31"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          #grad
          [attr.id]="gradientId"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="15.405"
          x2="32"
          y2="15.405"
        >
          <stop offset="0" stop-color="#7579ff" />
          <stop #stopMid offset="0.55" stop-color="#7579ff" />
          <stop #stopEnd offset="1" stop-color="#7579ff" />
        </linearGradient>
        <mask
          [attr.id]="maskId"
          maskUnits="userSpaceOnUse"
          x="-16"
          y="-16"
          width="64"
          height="64"
        >
          <rect x="-16" y="-16" width="64" height="64" fill="#fff" />
          <path
            #inner
            [attr.id]="innerId"
            d="M10.1544 3.48673C6.63803 3.48673 3.78993 6.33621 3.78993 9.8512V20.8123C3.78993 24.3272 6.63803 27.1767 10.1544 27.1767H21.8226C25.3372 27.1767 28.1871 24.3272 28.1871 20.8123V9.8512C28.1871 6.33621 25.3372 3.48673 21.8226 3.48673H10.1544Z"
            fill="#000"
          />
        </mask>
      </defs>
      <g [attr.mask]="'url(#' + maskId + ')'">
        <path
          #hex
          [attr.fill]="'url(#' + gradientId + ')'"
          d="M20.4012 0.0532939C22.9028 0.0680612 25.2294 1.47508 26.4917 3.7362L31.0511 11.9023C32.2975 14.1338 32.3169 16.859 31.1041 19.0592L26.573 27.281C25.3602 29.4812 23.0973 30.8252 20.6293 30.8107L11.5988 30.7575C9.0972 30.7426 6.77062 29.3357 5.50833 27.0745L0.948909 18.9086C-0.297467 16.6771 -0.316923 13.9519 0.895863 11.7517L5.42701 3.52973C6.6398 1.32955 8.90272 -0.0144295 11.3707 0.000116893L20.4012 0.0532939Z"
        />
      </g>
    </svg>
  `,
  styles: `
    :host {
      display: inline-flex;
      width: 32px;
      height: 30.81px;
    }

    svg {
      display: block;
      width: 32px;
      height: 31px;
    }

    /* The rotations are written as SVG transforms about explicit centres — the
       view-box reference box is what makes those centres mean viewBox units. */
    svg path,
    svg linearGradient {
      transform-box: view-box;
    }

    @media (prefers-reduced-motion: reduce) {
      svg.working {
        opacity: 0.7;
      }
    }
  `,
})
export class OmarkComponent {
  /** True while no answer text has started yet — the "just the symbol" phase. */
  public readonly working = input<boolean>(false);

  private readonly instanceId = `omark-${nextInstanceId++}`;
  protected readonly gradientId = `omGrad-${this.instanceId}`;
  protected readonly maskId = `omMask-${this.instanceId}`;
  protected readonly innerId = `omInner-${this.instanceId}`;

  private readonly hex = viewChild<ElementRef<SVGPathElement>>('hex');
  private readonly inner = viewChild<ElementRef<SVGPathElement>>('inner');
  private readonly grad = viewChild<ElementRef<SVGElement>>('grad');
  private readonly stopMid = viewChild<ElementRef<SVGStopElement>>('stopMid');
  private readonly stopEnd = viewChild<ElementRef<SVGStopElement>>('stopEnd');

  /**
   * The frame loops start outside the zone (as `teammate-avatar` does): they
   * only write SVG attributes, and inside it every frame would run app-wide
   * change detection — about 60 passes a second for a minutes-long wait.
   */
  private readonly ngZone = inject(NgZone);
  private frame = 0;
  private running = false;
  private pose = REST;

  constructor() {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    effect(() => {
      const working = this.working();
      if (prefersReducedMotion) {
        return;
      }
      if (working) {
        this.run();
      } else {
        this.settle();
      }
    });

    inject(DestroyRef).onDestroy(() => cancelAnimationFrame(this.frame));
  }

  private run(): void {
    if (this.running) {
      return;
    }
    this.running = true;
    cancelAnimationFrame(this.frame);
    const startedAt = performance.now();
    const tick = (now: number): void => {
      this.pose = poseAt(((now - startedAt) / 1000) * SPEED);
      this.apply(this.pose);
      this.frame = requestAnimationFrame(tick);
    };
    this.ngZone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(tick);
    });
  }

  /** Eases the live pose onto the next hex vertex (60°) and quarter-turn of
   * the cut-out (90°) so the mark comes to rest on its own silhouette, and
   * drops the tint back to the flat base colour. A mark that never ran just
   * snaps to rest. */
  private settle(): void {
    cancelAnimationFrame(this.frame);
    if (!this.running) {
      this.pose = REST;
      this.apply(REST);
      return;
    }
    this.running = false;
    const from = this.pose;
    const to: Pose = {
      hexRot: Math.ceil(from.hexRot / 60 - 1e-6) * 60,
      innerRot: Math.floor(from.innerRot / 90 + 1e-6) * 90,
      gradRot: from.gradRot,
      tint: 0,
    };
    const startedAt = performance.now();
    const tick = (now: number): void => {
      const k = Math.min(1, (now - startedAt) / SETTLE_MS);
      this.pose = lerpPose(from, to, easeOut(k));
      this.apply(this.pose);
      if (k < 1) {
        this.frame = requestAnimationFrame(tick);
      }
    };
    this.ngZone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(tick);
    });
  }

  private apply(pose: Pose): void {
    const hex = this.hex()?.nativeElement;
    const inner = this.inner()?.nativeElement;
    const grad = this.grad()?.nativeElement;
    const stopMid = this.stopMid()?.nativeElement;
    const stopEnd = this.stopEnd()?.nativeElement;
    // ponytail: the view may not exist yet on the effect's first run; the frame
    // loop is already scheduled and the next tick finds the elements.
    if (!hex || !inner || !grad || !stopMid || !stopEnd) {
      return;
    }
    hex.setAttribute('transform', `rotate(${pose.hexRot} ${CX} ${CY})`);
    inner.setAttribute(
      'transform',
      `rotate(${pose.innerRot} ${INNER_CX} ${INNER_CY})`,
    );
    grad.setAttribute(
      'gradientTransform',
      `rotate(${pose.gradRot} ${CX} ${CY})`,
    );
    stopMid.setAttribute('stop-color', mixHex(BASE, MID, pose.tint));
    stopEnd.setAttribute('stop-color', mixHex(BASE, END, pose.tint));
  }
}
